import { useEffect, useState } from "react";
import { UserPlus, Users, Trophy } from "lucide-react";
import { FamilyGoals } from "@/components/parent/FamilyGoal";
import { KidAvatar } from "@/components/live/KidAvatar";
import { http } from "@/lib/api";
import { useProfile } from "@/context/ProfileContext";
import { LinkChildForm, AddChildDialog, ChildCard } from "@/components/parent/FamilyParts";
import { AssessmentAlertsPopup } from "@/components/assessment/AssessmentAlerts";
import { WeeklySummary } from "@/components/parent/WeeklySummary";
import { ShareEarn } from "@/components/parent/ShareEarn";
import { ArchivedKids } from "@/components/parent/ManageChild";
import { ConsciousBaby } from "@/components/baby/ConsciousBaby";
import { useSearchParams } from "react-router-dom";

const KidTabs = ({ kids, sel, pick, met }) => (
  <div className="mt-8 flex flex-wrap gap-2 sticky top-20 z-10 py-2 bg-[#FDFBF7]/90 backdrop-blur" data-testid="kid-tabs" role="tablist">
    {[{ id: "all", name: "All kids" }, ...kids].map((k) => (
      <button key={k.id} role="tab" aria-selected={sel === k.id} onClick={() => pick(k.id)} data-testid={`kid-tab-${k.id}`}
        className={`inline-flex items-center gap-2 pl-2 pr-4 py-1.5 rounded-full border text-sm transition-colors ${sel === k.id ? "bg-[#2C4A3E] text-white border-[#2C4A3E]" : "bg-white border-[#E6DEC3] text-[#2C4A3E] hover:bg-[#F1EADB]"}`}>
        {k.id === "all" ? <Users className="w-5 h-5 ml-1" /> : <KidAvatar a={k.avatar} className="w-7 h-7" />}{k.name.split(" ")[0]}
        {met.has(k.id) && <Trophy className={`w-4 h-4 ${sel === k.id ? "text-[#F2D49B]" : "text-[#C87D53]"}`} data-testid={`kid-tab-goal-${k.id}`} aria-label="Weekly goal met" />}
      </button>
    ))}
  </div>
);

export default function ParentDashboard() {
  const { profile } = useProfile();
  const [kids, setKids] = useState(null);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useSearchParams();
  const [view, setView] = useState(() => q.get("view") || sessionStorage.getItem("cl_parent_view") || "kids");
  useEffect(() => { if (q.get("view")) { setView(q.get("view")); sessionStorage.setItem("cl_parent_view", q.get("view")); setQ({}, { replace: true }); } }, [q]); // eslint-disable-line react-hooks/exhaustive-deps
  const setViewTab = (v) => { setView(v); sessionStorage.setItem("cl_parent_view", v); };
  const [sel, setSel] = useState(() => sessionStorage.getItem("cl_parent_kid") || "all");
  const pick = (id) => { setSel(id); sessionStorage.setItem("cl_parent_kid", id); };
  const current = sel !== "all" && kids?.some((k) => k.id === sel) ? sel : "all";
  const shown = kids?.filter((k) => current === "all" || k.id === current);
  const [goals, setGoals] = useState(null);
  const loadGoals = () => http.get("/family/goals").then((r) => setGoals(r.data)).catch(() => {});
  const [tick, setTick] = useState(0);
  const load = () => { loadGoals(); setTick((t) => t + 1); return http.get("/family/children").then((r) => setKids(r.data)); };
  useEffect(() => { load(); }, []);
  const met = new Set((goals || []).filter((g) => g.goal?.met).map((g) => g.student_id));
  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-12 pb-24" data-testid="parent-dashboard">
      <span className="text-xs font-semibold uppercase tracking-[0.25em] text-[#C87D53]">Family · {profile.name}</span>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#2C4A3E] mt-3">My kids</h1>
      <p className="mt-4 text-stone-600 max-w-2xl">Approve live classes and School-Day Chat, see reminders, read every chat transcript and open your child's Class Memory Books.</p>
      {view === "kids" && <AssessmentAlertsPopup storageKey="cl_assess_alerts_parent" />}
      <div className="mt-8 inline-flex gap-1 p-1 rounded-full bg-[#F1EADB]" role="tablist" data-testid="parent-view-tabs">
        {[["kids", "My kids"], ["baby", "Conscious Baby"]].map(([v, l]) => <button key={v} role="tab" aria-selected={view === v} onClick={() => setViewTab(v)} data-testid={`parent-view-${v}`}
          className={`px-5 py-2 rounded-full text-sm transition-colors ${view === v ? "bg-[#2C4A3E] text-white" : "text-[#2C4A3E] hover:bg-white/70"}`}>{l}</button>)}
      </div>
      {view === "baby" ? <ConsciousBaby /> : <>
      {kids?.length > 1 && <KidTabs kids={kids} sel={current} pick={pick} met={met} />}
      <FamilyGoals goals={goals} only={current === "all" ? null : current} onSaved={loadGoals} />
      <WeeklySummary only={current === "all" ? null : current} />
      <div className="grid lg:grid-cols-12 gap-8 mt-10">
        <aside className="lg:col-span-4 space-y-6 min-w-0">
          <ShareEarn />
          <section className="rounded-3xl bg-[#F7F3EB] border border-[#E6DEC3] p-6">
            <p className="font-display text-xl text-[#2C4A3E]">Link an existing account</p>
            <p className="text-xs text-stone-500 mt-1 mb-4">Your child finds their 6-digit parent code on their Live page.</p>
            <LinkChildForm onDone={load} />
          </section>
          <section className="rounded-3xl bg-[#F7F3EB] border border-[#E6DEC3] p-6">
            <p className="font-display text-xl text-[#2C4A3E]">New to ConsciousLearn?</p>
            <p className="text-xs text-stone-500 mt-1 mb-4">Create a student login for your child in seconds.</p>
            <button onClick={() => setOpen(true)} data-testid="parent-add-child-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#C87D53] text-white text-sm"><UserPlus className="w-4 h-4" /> Create child account</button>
          </section>
          <ArchivedKids refreshKey={tick} onChange={load} />
        </aside>
        <div className="lg:col-span-8 space-y-6 min-w-0" data-testid="parent-children-list">
          {shown?.map((k) => <ChildCard key={k.id} k={k} onChange={load} />)}
          {kids && !kids.length && <p className="text-stone-500" data-testid="parent-no-children">No children linked yet. Link with a code or create an account.</p>}
          {!kids && <p className="text-stone-500">Loading…</p>}
        </div>
      </div>
      </>}
      <AddChildDialog open={open} setOpen={setOpen} onDone={(v) => (v === "baby" ? setViewTab("baby") : load())} />
    </div>
  );
}
